import { Temporal } from "temporal-polyfill";

export const WEEKDAYS = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
];

const DEFAULT_FROM = 800;
const DEFAULT_TILL = 2300;

export const hhmmToTime = (value) => {
    const padded = String(value).padStart(4, "0");
    return `${padded.slice(0, 2)}:${padded.slice(-2)}`;
};

export const timeToHhmm = (value) => {
    const match = /^(\d{2}):(\d{2})$/.exec(value || "");
    if (!match) {
        return null;
    }
    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    if (hours > 23 || minutes > 59) {
        return null;
    }
    return hours * 100 + minutes;
};

export const runsPastMidnight = (from, till) => from != null && till != null && till < from;

const zonedInstant = (date, time, timeZone) => {
    const [year, month, day] = date.split("-").map(Number);
    const [hour, minute] = time.split(":").map(Number);
    return Temporal.ZonedDateTime.from({
        timeZone,
        year,
        month,
        day,
        hour,
        minute,
        second: 0,
    }).toString({ timeZoneName: "never" });
};

const nextDate = (date) => Temporal.PlainDate.from(date).add({ days: 1 }).toString();

export const formatInLocation = (iso, timeZone) => {
    const zoned = Temporal.Instant.from(iso).toZonedDateTimeISO(timeZone);
    const pad = (value) => String(value).padStart(2, "0");
    return `${pad(zoned.day)}-${pad(zoned.month)}-${zoned.year}, ${pad(zoned.hour)}:${pad(zoned.minute)}`;
};

export const formatCalendarDate = (isoDate) => {
    const [year, month, day] = isoDate.split("-");
    return `${day}-${month}-${year}`;
};

export const weekDraftFromRows = (rows) => {
    const byDay = Object.fromEntries(WEEKDAYS.map((day) => [day, []]));
    for (const row of rows) {
        if (byDay[row.day]) {
            byDay[row.day].push(row);
        }
    }
    const days = [];
    const extras = [];
    for (const day of WEEKDAYS) {
        const [first, ...rest] = byDay[day];
        if (!first) {
            days.push({
                day,
                from: hhmmToTime(DEFAULT_FROM),
                till: hhmmToTime(DEFAULT_TILL),
                open: true,
                created: true,
            });
        } else {
            days.push({
                id: first.id,
                day,
                from: hhmmToTime(first.from),
                till: hhmmToTime(first.till),
                open: Boolean(first.open),
                created: false,
            });
            extras.push(...rest);
        }
    }
    return { days, extras };
};

export const buildWeekPayload = (days, deleteIds) => ({
    days: days.map((day) => {
        const from = timeToHhmm(day.from);
        const till = timeToHhmm(day.till);
        const entry = {
            day: day.day,
            from,
            till,
            open: day.open,
        };
        if (day.id) {
            entry.id = day.id;
        }
        return entry;
    }),
    deleteIds,
});

export const closureInterval = ({ mode, startDate, endDate, from, till, timeZone }) => {
    if (mode === "days") {
        return {
            start: zonedInstant(startDate, "00:00", timeZone),
            end: zonedInstant(nextDate(endDate), "00:00", timeZone),
        };
    }
    const fromHhmm = timeToHhmm(from);
    const tillHhmm = timeToHhmm(till);
    if (fromHhmm == null || tillHhmm == null || fromHhmm === tillHhmm) {
        return null;
    }
    const endOn = runsPastMidnight(fromHhmm, tillHhmm) ? nextDate(startDate) : startDate;
    return {
        start: zonedInstant(startDate, from, timeZone),
        end: zonedInstant(endOn, till, timeZone),
    };
};

const calendarDay = (value) => String(value).slice(0, 10);

const previousDate = (date) => Temporal.PlainDate.from(date).subtract({ days: 1 }).toString();

const instantAt = (date, time, timeZone) => Temporal.Instant.from(zonedInstant(date, time, timeZone));

// Slot generation keeps the oldest open row and lets one override replace that weekday.
export const effectiveHoursForDate = (rows, overrides, date) => {
    const covering = overrides.filter((row) => (
        calendarDay(row.start_date) <= date && calendarDay(row.end_date) >= date
    ));
    if (covering.length > 1) {
        return { status: "ambiguous" };
    }
    if (covering.length === 1) {
        return { status: "open", from: Number(covering[0].from), till: Number(covering[0].till) };
    }
    const weekday = WEEKDAYS[Temporal.PlainDate.from(date).dayOfWeek - 1];
    const row = rows
        .filter((item) => item.open && item.day === weekday)
        .sort((left, right) => left.id - right.id)[0];
    if (!row) {
        return { status: "closed" };
    }
    return { status: "open", from: Number(row.from), till: Number(row.till) };
};

const openWindow = (date, hours, timeZone) => {
    if (hours.status !== "open") {
        return null;
    }
    const endDate = runsPastMidnight(hours.from, hours.till) ? nextDate(date) : date;
    return {
        start: instantAt(date, hhmmToTime(hours.from), timeZone),
        end: instantAt(endDate, hhmmToTime(hours.till), timeZone),
    };
};

const coversWindow = (closure, window) => {
    const start = Temporal.Instant.from(closure.start);
    const end = Temporal.Instant.from(closure.end);
    return start.epochMilliseconds <= window.start.epochMilliseconds
        && end.epochMilliseconds >= window.end.epochMilliseconds;
};

const overlaps = (closure, start, end) => {
    const closureStart = Temporal.Instant.from(closure.start);
    const closureEnd = Temporal.Instant.from(closure.end);
    return closureStart.epochMilliseconds < end.epochMilliseconds
        && closureEnd.epochMilliseconds > start.epochMilliseconds;
};

export const calendarDateOf = (value) => {
    const date = value instanceof Date ? value : new Date(value);
    const pad = (part) => String(part).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

export const dayIsOutsideOpeningHours = (schedule, date, timeZone) => {
    const hours = effectiveHoursForDate(schedule.rows, schedule.overrides, date);
    const window = openWindow(date, hours, timeZone);
    if (!window) {
        return true;
    }
    return schedule.closures.some((closure) => coversWindow(closure, window));
};

export const timeIsOutsideOpeningHours = (schedule, { date, time, duration, timeZone }) => {
    if (!date || !time || !timeZone) {
        return false;
    }
    const start = instantAt(date, time, timeZone);
    const end = start.add({ minutes: duration || 0 });
    const windows = [date, previousDate(date)]
        .map((day) => openWindow(day, effectiveHoursForDate(schedule.rows, schedule.overrides, day), timeZone))
        .filter(Boolean);
    const inside = windows.some((window) => (
        start.epochMilliseconds >= window.start.epochMilliseconds
        && end.epochMilliseconds <= window.end.epochMilliseconds
    ));
    if (!inside) {
        return true;
    }
    return schedule.closures.some((closure) => overlaps(closure, start, end));
};

const normalForWeekday = (rows, weekday) => {
    const row = rows
        .filter((item) => item.day === weekday)
        .sort((left, right) => left.id - right.id)[0];
    if (row?.open) {
        return { open: true, from: Number(row.from), till: Number(row.till) };
    }
    return { open: false };
};

// Effective opening for one calendar date, enriched for a read-only display:
// `exception` flags when this date deviates from the plain weekly template
// (an extra opening, or a closure), so a view can show the real hours with a
// muted "normally ..." line. Reuses the same rows+overrides+closures logic the
// booking validation uses, so there is one source of truth.
export const weekScheduleForDate = (schedule, date, timeZone) => {
    const eff = effectiveHoursForDate(schedule.rows, schedule.overrides, date);
    const hasOverride = schedule.overrides.some(
        (row) => calendarDay(row.start_date) <= date && calendarDay(row.end_date) >= date,
    );
    const weekday = WEEKDAYS[Temporal.PlainDate.from(date).dayOfWeek - 1];
    const normal = normalForWeekday(schedule.rows, weekday);

    let closure = "none";
    if (eff.status === "open") {
        const window = openWindow(date, eff, timeZone);
        if (window) {
            if (schedule.closures.some((row) => coversWindow(row, window))) {
                closure = "full";
            } else if (schedule.closures.some((row) => overlaps(row, window.start, window.end))) {
                closure = "partial";
            }
        }
    }

    if (closure === "full") {
        return { status: "closed", exception: "tempClosed", normal };
    }
    if (eff.status === "ambiguous") {
        return { status: "varies", exception: "extra", normal };
    }
    if (eff.status === "open") {
        const exception = hasOverride ? "extra" : closure === "partial" ? "partialClosed" : null;
        return { status: "open", from: eff.from, till: eff.till, exception, normal };
    }
    return { status: "closed", exception: null, normal };
};

// Weekday (Monday..Sunday) of an instant in the location's timezone. A booking at 22:00 UTC
// can land on a different weekday locally, so counting must use the location zone, not UTC.
export const weekdayInZone = (iso, timeZone) =>
    WEEKDAYS[Temporal.Instant.from(iso).toZonedDateTimeISO(timeZone).dayOfWeek - 1];

// Count booking start instants whose local weekday is in `weekdays`. A booking counts on its
// start only (no duration overlap).
export const countBookingsOnWeekdays = (bookingDates, weekdays, timeZone) => {
    const set = new Set(weekdays);
    if (set.size === 0) {
        return 0;
    }
    return bookingDates.reduce(
        (total, iso) => total + (set.has(weekdayInZone(iso, timeZone)) ? 1 : 0),
        0,
    );
};

// The API names the row ("opening-hour override 17"). Operators need the rule, not the id.
export const operatorScheduleError = (message, fallback) => {
    const raw = String(message || "");
    if (/overlaps opening-hour override/i.test(raw)) {
        return "These dates already have extra opening hours. Change or remove that row, or pick dates outside it.";
    }
    return raw || fallback;
};

export const overridePayload = ({ startDate, endDate, from, till }) => {
    const fromHhmm = timeToHhmm(from);
    const tillHhmm = timeToHhmm(till);
    if (fromHhmm == null || tillHhmm == null || fromHhmm === tillHhmm) {
        return null;
    }
    return {
        start_date: startDate,
        end_date: endDate,
        from: fromHhmm,
        till: tillHhmm,
    };
};
